import React, { useState, useEffect, useMemo } from "react";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const ColumnGroupingTable = ({
  columns,
  datas,
  loading,
  freezeParentColumnCount = 0,
  disablePagination = false,
  rowSpanKey = null,
  tableHeight,
  applyThousandsSeparator = false,
  hideHeader = false,
  hideParentRowHeader = false,
  hideChildRowHeader = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [columnWidths, setColumnWidths] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleToggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const flatColumns = columns.flatMap((col) => col.children || col);

  useEffect(() => {
    const initialWidths = flatColumns.map(() => 0);
    const tableCells = document.querySelectorAll(".MuiTableCell-root");
    tableCells.forEach((cell, index) => {
      const width = cell.offsetWidth;
      if (index < freezeParentColumnCount) {
        initialWidths[index] = width;
      }
    });

    setColumnWidths((prevWidths) => {
      const hasChanged = initialWidths.some(
        (width, i) => width !== prevWidths[i],
      );
      return hasChanged ? initialWidths : prevWidths;
    });
  }, [flatColumns, freezeParentColumnCount]);

  const processedData = useMemo(() => {
    if (!rowSpanKey) return datas;

    const result = [];
    let i = 0;

    while (i < datas.length) {
      const currentValue = datas[i][rowSpanKey];
      let rowSpanCount = 1;

      for (let j = i + 1; j < datas.length; j++) {
        if (datas[j][rowSpanKey] === currentValue) {
          rowSpanCount++;
        } else {
          break;
        }
      }

      result.push({ ...datas[i], rowSpan: rowSpanCount });

      for (let k = i + 1; k < i + rowSpanCount; k++) {
        result.push({ ...datas[k], rowSpan: 0 });
      }

      i += rowSpanCount;
    }

    return result;
  }, [datas, rowSpanKey]);

  const uniqueRows = useMemo(() => {
    if (!rowSpanKey) return datas;
    return processedData.filter((row) => row.rowSpan > 0);
  }, [processedData, rowSpanKey, datas]);

  const paginatedUniqueRows = disablePagination
    ? uniqueRows
    : uniqueRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const paginatedData = useMemo(() => {
    if (!rowSpanKey)
      return disablePagination
        ? datas
        : datas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const result = [];
    paginatedUniqueRows.forEach((uniqueRow) => {
      result.push(uniqueRow);
      if (uniqueRow.rowSpan > 1) {
        const startIndex = processedData.indexOf(uniqueRow);
        for (let i = 1; i < uniqueRow.rowSpan; i++) {
          result.push(processedData[startIndex + i]);
        }
      }
    });
    return result;
  }, [
    paginatedUniqueRows,
    processedData,
    rowSpanKey,
    disablePagination,
    page,
    rowsPerPage,
    datas,
  ]);

  const formatForDisplay = (value) => {
    if (value === null || value === undefined || value === "") {
      return value;
    }

    if (Object.is(value, -0)) {
      return applyThousandsSeparator ? "-0" : "-0";
    }

    if (!applyThousandsSeparator) {
      return value;
    }

    const valueString = String(value);
    const numericValue = Number(valueString.replace(/,/g, ""));

    if (isNaN(numericValue)) {
      return value;
    }

    const [integerPart, decimalPart] = valueString.split(".");

    const absIntegerPart = Math.abs(Number(integerPart));
    const isNegative = Number(integerPart) < 0 || value < 0;

    const formattedIntegerPart = new Intl.NumberFormat("en-US").format(
      absIntegerPart,
    );

    const result = (isNegative ? "-" : "") + formattedIntegerPart;

    return decimalPart !== undefined ? `${result}.${decimalPart}` : result;
  };

  const renderRows = (dataRows, level = 0) => {
    return dataRows.flatMap((data, rowIndex) => {
      const isParent = data.children && data.children.length > 0;
      const isExpanded = expandedRows[data.id];

      const rowsToRender = [
        <TableRow
          hover
          role="checkbox"
          tabIndex={-1}
          key={data.id}
          sx={{ backgroundColor: level > 0 ? "#f0f0f0" : "transparent" }}
        >
          {flatColumns.map((column, colIndex) => {
            const value = data[column.id];
            const isFrozen = colIndex < freezeParentColumnCount;
            const leftPosition = columnWidths
              .slice(0, colIndex)
              .reduce((acc, width) => acc + width, 0);

            if (column.id === rowSpanKey && data.rowSpan === 0) {
              return null;
            }

            const rowSpanValue =
              column.id === rowSpanKey ? data.rowSpan : undefined;

            const rowLevelStyles = data?.cellStyles || {};
            const columnSpecificStyles =
              data?.dynamicCellStyles?.[column.id] || {};

            const effectiveCellStyles = {
              ...rowLevelStyles,
              ...columnSpecificStyles,
            };

            const cellBackgroundColor =
              rowLevelStyles.backgroundColor ||
              (isFrozen ? "#fff" : "transparent");

            const paddingLeft = colIndex === 0 ? level * 10 : 0;
            const commonCellProps = {
              key: column.id,
              align: column?.align || "left",
              sx: {
                position: isFrozen ? "sticky" : "static",
                left: isFrozen ? `${leftPosition}px` : "auto",
                backgroundColor: cellBackgroundColor,
                zIndex: isFrozen ? 1 : "auto",
                borderRight: "1px solid rgba(224, 224, 224, 1)",
                fontSize: level > 0 ? "0.75rem" : "0.8rem",
                fontStyle: level > 0 ? "italic" : "normal",
                padding: "6px",
                paddingLeft: `${6 + paddingLeft}px`,
                minWidth: column?.minWidth,
                maxWidth: column?.maxWidth,
                wordBreak: "break-word",
                verticalAlign: "middle",
                ...effectiveCellStyles,
              },
              rowSpan: rowSpanValue,
            };

            if (colIndex === 0) {
              return (
                <TableCell
                  {...commonCellProps}
                  sx={{
                    ...commonCellProps.sx,
                    whiteSpace: "pre-wrap",
                    overflowX: "visible",
                  }}
                >
                  {isParent ? (
                    <IconButton
                      size="small"
                      onClick={() => handleToggleExpand(data.id)}
                      aria-label={isExpanded ? "collapse" : "expand"}
                      sx={{ mr: 0.5 }}
                    >
                      {isExpanded ? (
                        <RemoveIcon fontSize="small" />
                      ) : (
                        <AddIcon fontSize="small" />
                      )}
                    </IconButton>
                  ) : (
                    <span style={{ marginLeft: `${2}px` }}></span>
                  )}
                  {value}
                </TableCell>
              );
            }

            return (
              <TableCell {...commonCellProps}>
                {column.format
                  ? column.format(value)
                  : !isNaN(Number(value))
                    ? formatForDisplay(value)
                    : String(value)}
              </TableCell>
            );
          })}
        </TableRow>,
      ];

      if (isParent && isExpanded) {
        rowsToRender.push(...renderRows(data.children, level + 1));
      }

      return rowsToRender;
    });
  };

  return (
    <>
      {loading ? (
        <>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={56}
            animation="pulse"
            style={{ marginBottom: 8 }}
          />
          {Array.from({ length: 10 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width="100%"
              height={40}
              animation="pulse"
              style={{ marginBottom: 8 }}
            />
          ))}
        </>
      ) : (
        <>
          <TableContainer
            sx={{ height: tableHeight ?? "auto", overflowX: "auto" }}
          >
            <Table stickyHeader aria-label="sticky table">
              {!hideHeader && (
                <TableHead
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    "& .MuiTableCell-root": {
                      borderRight: "1px solid rgba(224, 224, 224, 1)",
                    },
                  }}
                >
                  {/* First Row: Group Headers */}
                  {!hideParentRowHeader && (
                    <TableRow>
                      {columns.map((column, index) => {
                        const isFrozen = index < freezeParentColumnCount;
                        const leftPosition = columnWidths
                          .slice(0, index)
                          .reduce((acc, width) => acc + width, 0);

                        return column.children ? (
                          <TableCell
                            key={column.label}
                            align={column?.align || "center"}
                            colSpan={column.children.length}
                            sx={{
                              backgroundColor: "#eeeeee",
                              fontWeight: "bold",
                              color: "#000000",
                              fontSize: "0.8rem",
                              minWidth: column?.minWidth,
                              maxWidth: column?.maxWidth,
                              wordBreak: "break-word",
                            }}
                          >
                            {column.label}
                          </TableCell>
                        ) : (
                          <TableCell
                            key={column.id}
                            rowSpan={2}
                            align={column?.align || "center"}
                            sx={{
                              position: isFrozen ? "sticky" : "static",
                              left: isFrozen ? `${leftPosition}px` : "auto",
                              backgroundColor: "#eeeeee",
                              zIndex: isFrozen ? 3 : "auto",
                              borderRight: "1px solid rgba(224, 224, 224, 1)",
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              minWidth: column?.minWidth,
                              maxWidth: column?.maxWidth,
                              wordBreak: "break-word",
                            }}
                          >
                            {column.label}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )}

                  {/* Second Row: Child Columns */}
                  {!hideChildRowHeader && (
                    <TableRow>
                      {columns.map((column) => {
                        return column.children
                          ? column.children.map((child) => (
                              <TableCell
                                key={child.id}
                                align={column?.align || "center"}
                                sx={{
                                  backgroundColor: "#f5f5f5",
                                  fontWeight: "bold",
                                  fontSize: "0.8rem",
                                  minWidth: child?.minWidth,
                                  maxWidth: column?.minWidth,
                                  wordBreak: "break-word",
                                }}
                              >
                                {child.label}
                              </TableCell>
                            ))
                          : null;
                      })}
                    </TableRow>
                  )}
                </TableHead>
              )}
              <TableBody>{renderRows(paginatedData)}</TableBody>
            </Table>
          </TableContainer>
          {!disablePagination && (
            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 50, 100]}
              component="div"
              count={rowSpanKey ? uniqueRows.length : datas.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
            />
          )}
        </>
      )}
    </>
  );
};

export default ColumnGroupingTable;
